import { useState, useEffect, useCallback } from 'react';
import { DialogTrigger, Dialog, Modal, Heading, DateValue, Label } from 'react-aria-components';
import { useForm, SubmitHandler } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import Button from 'src/components/Button/Button';
import InputText from 'src/components/InputText/InputText';
import RaddioButton from 'src/components/RadioButton/RaddioButton';
import SelectField from 'src/components/SelectField/SelectField';
import TimeField from 'src/components/TimeField/TimeField';
import * as yup from 'yup';

import {
  createReservation,
  updateReservation,
  UpsertAttendanceReservationRequest,
} from '@/api/createAttendanceReservation';
import { getAttendanceReservationByIDWithLineID } from '@/api/getAttendanceReservations';
import { getChildOfficesByLineID } from '@/api/getChildOffices';
import { getLineUserCalendar } from '@/api/getLineUserCalendar';
import TextAreaInput from '@/components/TextArea/TextAreaInput';
import { LineUserAtom } from '@/jotai/jotai';
import { formattedDate } from '@/logics/date/date';
import { guardianRelationshipOptions } from '@/logics/guardian/guardian';
import { DetailedAttendanceReservation } from '@/types/attendanceReservation';
import { Child } from '@/types/child';
import { MonthlyCalendarItem } from '@/types/monthlyCalendar';
import { Office } from '@/types/office';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedDate: DateValue | undefined;
  child: Child | undefined;
  resID?: number;
  afterSubmit: () => void;
};

type FormData = {
  office_id: number;
  attendance_status: 'absent' | 'attend';
  absent_reason?: string;
  pickup_info?: {
    isRequired?: string;
    pickup_time?: string;
    pickup_location?: string;
    pickup_companion?: string;
    arrival_time?: string;
  };
  dropoff_info?: {
    isRequired?: string;
    dropoff_time?: string;
    dropoff_companion?: string;
    departure_time?: string;
  };
  is_dinner?: string;
  notes?: string;
};

export const AttendanceReservationFormDialog = ({
  isOpen,
  setIsOpen,
  selectedDate,
  child,
  resID,
  afterSubmit,
}: Props) => {
  const [displayDate, setDisplayDate] = useState<string>();
  const [lineUser, _] = useAtom(LineUserAtom);
  const [reservation, setReservation] = useState<DetailedAttendanceReservation>();
  const [calendarInfo, setCalendarInfo] = useState<MonthlyCalendarItem>();
  const [offices, setOffices] = useState<Office[]>([]);

  // 選択した日に予定がはいってるかどうかの確認
  useEffect(() => {
    const fetchReservation = async () => {
      const result = await getAttendanceReservationByIDWithLineID(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? '',
        child?.id ?? 0,
        resID ?? 0
      );
      if (result.status === 200) {
        setReservation(result.result);
        reset(reservationToFormData(result.result));
        console.log(result.result);
      }
    };
    if (selectedDate && child && resID) {
      fetchReservation();
    }
    if (selectedDate) {
      fetchLineUserCalendar();
    }
    fetchChildOffices();
  }, []);

  const fetchLineUserCalendar = useCallback(async () => {
    const date = `${selectedDate?.year}-${selectedDate?.month.toString().padStart(2, '0')}-${selectedDate?.day.toString().padStart(2, '0')}`;
    try {
      const result = await getLineUserCalendar(lineUser?.accessToken ?? '', date);
      if (result.status === 200) {
        setCalendarInfo(result.result);
      }
    } catch (error) {
      console.log('error');
    }
  }, [selectedDate]);

  const fetchChildOffices = useCallback(async () => {
    try {
      const result = await getChildOfficesByLineID(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? '',
        child?.id ?? 0
      );
      if (result.status === 200) {
        setOffices(result.result);
      }
    } catch (error) {
      console.log('error');
    }
  }, [child]);

  const schema: yup.ObjectSchema<FormData> = yup.object({
    office_id: yup.number().required(),
    attendance_status: yup.string().oneOf(['absent', 'attend']).required(),

    absent_reason: yup.string().when('attendance_status', {
      is: (status: string) => status === 'absent',
      then: (schema) => schema.required('欠席の場合は必須です'),
    }),

    // お迎え
    pickup_info: yup.object().when('attendance_status', {
      is: 'attend',
      then: (schema) =>
        schema.shape({
          isRequired: yup.string().required('利用の場合は必須です'),
          pickup_time: yup.string().when('isRequired', {
            is: 'true',
            then: (schema) => schema.required('お迎えが必要な場合は必須です'),
          }),
          pickup_location: yup.string().when('isRequired', {
            is: 'true',
            then: (schema) => schema.required('お迎えが必要な場合は必須です'),
          }),
          pickup_companion: yup.string().when('isRequired', {
            is: 'false',
            then: (schema) => schema.required('お迎えが必要ない場合は必須です'),
          }),
          arrival_time: yup.string().when('isRequired', {
            is: 'false',
            then: (schema) => schema.required('お迎えが必要ない場合は必須です'),
          }),
        }),
    }),

    // お送り
    dropoff_info: yup.object().when('attendance_status', {
      is: 'attend',
      then: (schema) =>
        schema.shape({
          isRequired: yup.string().required('利用の場合は必須です'),
          dropoff_time: yup.string().when('isRequired', {
            is: 'true',
            then: (schema) => schema.required('お送りが必要な場合は必須です'),
          }),
          dropoff_companion: yup.string().when('isRequired', {
            is: 'false',
            then: (schema) => schema.required('お送りが必要ない場合は必須です'),
          }),
          departure_time: yup.string().when('isRequired', {
            is: 'false',
            then: (schema) => schema.required('お送りが必要ない場合は必須です'),
          }),
        }),
    }),

    is_dinner: yup.string().when('attendance_status', {
      is: 'attend',
      then: (schema) => schema.required('利用の場合は必須です').default('false'),
    }),
    notes: yup.string(),
  });

  const { register, handleSubmit, control, setValue, watch, reset } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: reservation
      ? reservationToFormData(reservation)
      : {
          attendance_status: 'attend',
          pickup_info: {
            isRequired: 'false',
            pickup_location: 'school',
          },
          dropoff_info: {
            isRequired: 'false',
          },
          is_dinner: 'false',
        },
  });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!lineUser) return;
    if (!selectedDate) return;

    const targetDate = selectedDate.toDate('Asia/Tokyo');
    const request: UpsertAttendanceReservationRequest = {
      date: formattedDate(targetDate, 'yyyy-MM-dd'),
      office_id: data.office_id,
      attendance_status: data.attendance_status,
    };

    if (data.attendance_status === 'absent') {
      request.absent_reason = data.absent_reason;
    } else {
      request.pickup_info = {
        isRequired: data.pickup_info?.isRequired === 'true',
      };
      request.dropoff_info = {
        isRequired: data.dropoff_info?.isRequired === 'true',
      };
      request.is_dinner = data.is_dinner === 'true';
      request.notes = data.notes;

      // お迎えが必要な場合と必要ない場合で分岐
      if (data.pickup_info?.isRequired === 'true') {
        request.pickup_info.pickup_time = data.pickup_info.pickup_time;
        request.pickup_info.pickup_location = data.pickup_info.pickup_location;
      } else {
        request.pickup_info.pickup_companion = data.pickup_info?.pickup_companion;
        request.pickup_info.arrival_time = data.pickup_info?.arrival_time;
      }

      // お送りが必要な場合と必要ない場合で分岐
      if (data.dropoff_info?.isRequired === 'true') {
        request.dropoff_info.dropoff_time = data.dropoff_info.dropoff_time;
      } else {
        request.dropoff_info.dropoff_companion = data.dropoff_info?.dropoff_companion;
        request.dropoff_info.departure_time = data.dropoff_info?.departure_time;
      }
    }

    if (resID) {
      // 更新処理
      const result = await updateReservation(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? '',
        child?.id ?? 0,
        resID,
        request
      );
      if (result.status === 200) {
        alert('来所予約を更新しました');
        afterSubmit();
      } else {
        console.log('error');
      }
    } else {
      const result = await createReservation(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? '',
        child?.id ?? 0,
        request
      );
      if (result.status === 200) {
        console.log(result.result);
        alert('来所予約を保存しました');
        afterSubmit();
      } else {
        console.log('error');
      }
    }

    setIsOpen(false);
  };

  // 日付のフォーマット
  useEffect(() => {
    if (selectedDate) {
      setDisplayDate(formattedDate(selectedDate.toDate('Asia/Tokyo'), 'yyyy年MM月dd日（E）'));
    }
  }, [selectedDate]);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Dialog className="h-full w-full overflow-auto pb-80">
          <div className="outline-non m-4 h-max overflow-y-auto rounded-lg border border-gray-400 bg-white py-4 shadow-lg">
            <Heading
              level={2}
              className="mb-6 text-xl font-bold"
            >
              <div className="mx-4 rounded-8 bg-blue-100 px-2 py-4">
                <Heading className="text-lg font-bold">
                  {child?.last_name + ' ' + child?.first_name}
                </Heading>
                <Heading className="text-lg font-bold">{displayDate}</Heading>
              </div>
            </Heading>
            <form
              className="space-y-4"
              onSubmit={handleSubmit(onSubmit, (errors) => {
                console.log('Validation errors:', errors);
              })}
            >
              <div className="mx-2 flex flex-row space-x-4">
                <Button
                  className="w-full"
                  size="sm"
                  variant={watch('attendance_status') === 'attend' ? 'solid-fill' : 'outline'}
                  onClick={() => setValue('attendance_status', 'attend')}
                >
                  利用
                </Button>
                <Button
                  className="w-full"
                  size="sm"
                  variant={watch('attendance_status') === 'absent' ? 'solid-fill' : 'outline'}
                  onClick={() => setValue('attendance_status', 'absent')}
                >
                  欠席
                </Button>
              </div>

              <div className="bg-green-200 p-3 font-bold">
                <Label>事業所</Label>
              </div>
              <div className="mx-2 flex flex-col space-y-4">
                <SelectField
                  label="施設名"
                  name="office_id"
                  control={control}
                  items={offices.map((office) => ({
                    label: office.name,
                    value: office.id.toString(),
                  }))}
                />
              </div>

              {watch('attendance_status') === 'absent' ? (
                <>
                  <div className="bg-green-200 p-3 font-bold">
                    <Label>欠席理由</Label>
                  </div>
                  <div className="mx-2 flex flex-col space-y-4">
                    <TextAreaInput {...register('absent_reason')} />
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-green-200 p-3 font-bold">
                    <Label>お迎え</Label>
                  </div>
                  <div className="mx-2 flex flex-col space-y-4">
                    <RaddioButton
                      label="お迎え（来所）"
                      name="pickup_info.isRequired"
                      control={control}
                      items={[
                        { label: 'なし', value: 'false' },
                        { label: 'あり', value: 'true' },
                      ]}
                    />

                    <hr />

                    {watch('pickup_info.isRequired') === 'true' ? (
                      <>
                        <TimeField
                          label="希望お迎え時刻"
                          name="pickup_info.pickup_time"
                          control={control}
                        />
                        <hr />
                        <RaddioButton
                          label="希望お迎え場所"
                          name="pickup_info.pickup_location"
                          control={control}
                          items={[
                            { label: '学校', value: 'school' },
                            { label: '自宅', value: 'home' },
                          ]}
                        />
                      </>
                    ) : (
                      <>
                        <SelectField
                          label="同行者"
                          name="pickup_info.pickup_companion"
                          control={control}
                          items={guardianRelationshipOptions.map((option) => ({
                            label: option.label,
                            value: option.value,
                          }))}
                        />
                        <hr />
                        <TimeField
                          label="来所予定時刻"
                          name="pickup_info.arrival_time"
                          control={control}
                        />
                      </>
                    )}
                  </div>

                  <div className="bg-green-200 p-3 font-bold">
                    <Label>お送り</Label>
                  </div>
                  <div className="mx-2 flex flex-col space-y-4">
                    <RaddioButton
                      label="お送り（出所）"
                      name="dropoff_info.isRequired"
                      control={control}
                      items={[
                        { label: 'なし', value: 'false' },
                        { label: 'あり', value: 'true' },
                      ]}
                    />

                    <hr />

                    {watch('dropoff_info.isRequired') === 'true' ? (
                      <TimeField
                        label="希望出所時刻"
                        name="dropoff_info.dropoff_time"
                        control={control}
                      />
                    ) : (
                      <>
                        <SelectField
                          label="同行者"
                          name="dropoff_info.dropoff_companion"
                          control={control}
                          items={guardianRelationshipOptions.map((option) => ({
                            label: option.label,
                            value: option.value,
                          }))}
                        />

                        <hr />

                        <TimeField
                          label="出所予定時刻"
                          name="dropoff_info.departure_time"
                          control={control}
                        />
                      </>
                    )}
                  </div>
                  <div className="bg-green-200 p-3 font-bold">
                    <Label>夕食</Label>
                  </div>
                  <div className="mx-2 flex flex-col space-y-4">
                    <Label>※第２週のみ、長期休暇中は昼食の提供</Label>
                    <RaddioButton
                      name="is_dinner"
                      control={control}
                      items={[
                        { label: 'なし', value: 'false' },
                        { label: 'あり', value: 'true' },
                      ]}
                    />
                    <Label>夕食メニュー</Label>
                    <Label>{calendarInfo ? calendarInfo.dinner_menu : ''}</Label>
                  </div>

                  <div className="bg-green-200 p-3 font-bold">
                    <Label>イベント</Label>
                  </div>
                  <div className="mx-2 flex flex-col space-y-4">
                    <Label>{calendarInfo ? calendarInfo.event_title : ''}</Label>
                    <hr />
                    <span />
                  </div>

                  <div className="bg-green-200 p-3 font-bold">
                    <Label>備考</Label>
                  </div>
                  <div className="mx-2 flex flex-col space-y-4">
                    <InputText
                      type="textarea"
                      {...register('notes')}
                    />
                  </div>
                </>
              )}

              <div className="mt-10 flex flex-row items-center justify-center space-x-2">
                <Button
                  type="submit"
                  size="lg"
                  variant="solid-fill"
                >
                  保存
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                >
                  戻る
                </Button>
              </div>
            </form>
          </div>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};

function reservationToFormData(reservation: DetailedAttendanceReservation): FormData {
  const formData: FormData = {
    office_id: reservation.office.id,
    attendance_status: reservation.attendance_status === 'attend' ? 'attend' : 'absent',
  };

  if (reservation.attendance_status === 'absent') {
    formData.absent_reason = reservation.absent_reason;
  } else {
    formData.pickup_info = {
      isRequired: reservation.pickup_info?.isRequired ? 'true' : 'false',
    };
    formData.dropoff_info = {
      isRequired: reservation.dropoff_info?.isRequired ? 'true' : 'false',
    };

    if (reservation.pickup_info?.isRequired) {
      formData.pickup_info.pickup_time = reservation.pickup_info.pickup_time;
      formData.pickup_info.pickup_location = reservation.pickup_info.pickup_location;
    } else {
      formData.pickup_info.pickup_companion = reservation.pickup_info?.pickup_companion;
      formData.pickup_info.arrival_time = reservation.pickup_info?.arrival_time;
    }

    if (reservation.dropoff_info?.isRequired) {
      formData.dropoff_info.dropoff_time = reservation.dropoff_info?.dropoff_time;
    } else {
      formData.dropoff_info.dropoff_companion = reservation.dropoff_info?.dropoff_companion;
      formData.dropoff_info.departure_time = reservation.dropoff_info?.departure_time;
    }

    formData.is_dinner = reservation.is_dinner ? 'true' : 'false';
    formData.notes = reservation.notes;
  }

  return formData;
}
